/**
 * Configuration — Trade Categories
 * Classification: Paired Configuration
 * Source register: Trades Poster — Trade Categories
 *
 * Controls where and how saved Trade Categories, Subcategories and
 * public-facing Subcategories are displayed and used. Retrieves permanent
 * source records by ID. Does not create, rename, re-parent, or alter source data.
 */

import { buildSampleTradeCategories } from '../sections/AdminDashboard/TradeCategories/tradeCategoriesDummyData';
import { getTradeSubcategories } from './tradeCategoryWorkflow';

export const ALL_SCOPE_SELECTION_LABEL =
  'All current and future Trade Categories and Subcategories';

export const APPLICATION_AREA_OPTIONS = [
  { value: 'Business Signup', label: 'Business Signup' },
  { value: 'Business Profile', label: 'Business Profile' },
  { value: 'Post Creation', label: 'Post Creation' },
  { value: 'Public Browse', label: 'Public Browse' },
];

export const APPLICATION_AREA_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  ...APPLICATION_AREA_OPTIONS,
];

export const RULE_SCOPE = {
  ALL_TRADE_CATEGORIES: 'All Trade Categories',
  SELECTED_TRADE_CATEGORIES: 'Selected Trade Categories',
  SELECTED_SUBCATEGORIES: 'Selected Subcategories',
};

export const RULE_SCOPE_OPTIONS = [
  { value: RULE_SCOPE.ALL_TRADE_CATEGORIES, label: RULE_SCOPE.ALL_TRADE_CATEGORIES },
  { value: RULE_SCOPE.SELECTED_TRADE_CATEGORIES, label: RULE_SCOPE.SELECTED_TRADE_CATEGORIES },
  { value: RULE_SCOPE.SELECTED_SUBCATEGORIES, label: RULE_SCOPE.SELECTED_SUBCATEGORIES },
];

export const CONFIGURATION_STATUS = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
};

export const CONFIGURATION_STATUS_OPTIONS = [
  { value: CONFIGURATION_STATUS.ACTIVE, label: 'Active' },
  { value: CONFIGURATION_STATUS.INACTIVE, label: 'Inactive' },
];

export const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  ...CONFIGURATION_STATUS_OPTIONS,
];

export const DISPLAY_OPTIONS = [
  { value: 'Show', label: 'Show' },
  { value: 'Hide', label: 'Hide' },
];

export const MESSAGES = {
  empty: 'No Trade Category configuration rules have been added.',
  saved: 'Trade Category configuration rule saved successfully.',
  updated: 'Trade Category configuration rule updated successfully.',
  activated: 'Trade Category configuration rule activated successfully.',
  inactivated: 'Trade Category configuration rule made Inactive successfully.',
  overlap:
    'This rule overlaps another Active rule in the same Application Area. Adjust the Application Area or Scope Selection before saving.',
};

export function formatConfigAuditDisplay(value, by) {
  if (!value || !String(value).trim()) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    const dateStr = d.toLocaleString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    if (by?.trim()) return `${dateStr} · ${by}`;
    return dateStr;
  } catch {
    return String(value);
  }
}

export function formatConfigListDate(value) {
  if (!value || !String(value).trim()) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return '—';
  }
}

/** Screen 1 Last Updated: "03 Aug 2026 — Platform Owner" */
export function formatConfigLastUpdatedDisplay(value, by) {
  if (!value || !String(value).trim()) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return by?.trim() ? String(by) : '—';
    const dateStr = d.toLocaleString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    if (by?.trim()) return `${dateStr} — ${by.trim()}`;
    return dateStr;
  } catch {
    return '—';
  }
}

export function formatSourceLabel(name, id) {
  if (!name && !id) return '—';
  if (!name) return String(id);
  if (!id) return String(name);
  return `${name} — ${id}`;
}

/**
 * Retrieves permanent source records from Trades Poster — Trade Categories.
 * Configuration never seeds or modifies these records.
 */
export function getTradeCategoryConfigSources() {
  return buildSampleTradeCategories();
}

export function getSourceTradeCategoryOptions(sources = getTradeCategoryConfigSources()) {
  return (sources || [])
    .map((c) => ({ id: c.id, name: c.name, label: formatSourceLabel(c.name, c.id) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function getSourceSubcategoryOptions(
  parentIds = [],
  sources = getTradeCategoryConfigSources(),
) {
  const parents = new Set((parentIds || []).filter(Boolean));
  const options = [];
  (sources || []).forEach((category) => {
    if (parents.size && !parents.has(category.id)) return;
    getTradeSubcategories(category).forEach((sub) => {
      options.push({
        id: sub.id,
        name: sub.name,
        parentId: category.id,
        parentName: category.name,
        displayAsPublicFacing: Boolean(sub.displayAsPublicFacing),
        label: formatSourceLabel(sub.name, sub.id),
        parentLabel: formatSourceLabel(category.name, category.id),
      });
    });
  });
  return options.sort((a, b) => a.name.localeCompare(b.name));
}

export function nextTradeCategoryRuleId(existing = []) {
  const nums = (existing || [])
    .map((r) => {
      const match = String(r.id || '').match(/^CTC-(\d+)$/i);
      return match ? Number(match[1]) : 0;
    })
    .filter((n) => n > 0);
  const next = nums.length ? Math.max(...nums) + 1 : 1;
  return `CTC-${String(next).padStart(4, '0')}`;
}

export function emptyTradeCategoryRuleForm() {
  return {
    id: '',
    ruleName: '',
    applicationArea: '',
    ruleScope: '',
    tradeCategoryIds: [],
    parentTradeCategoryIds: [],
    subcategoryIds: [],
    tradeCategoryDisplay: '',
    subcategoryDisplay: '',
    publicFacingDisplay: '',
    configurationStatus: CONFIGURATION_STATUS.ACTIVE,
    createdBy: '',
    createdOn: '',
    lastUpdatedBy: '',
    lastUpdatedOn: '',
    changeHistory: [],
  };
}

export function buildRuleBehaviourSummary(form = {}, sources = getTradeCategoryConfigSources()) {
  const area = form.applicationArea || 'the selected Application Area';
  const showCategories = form.tradeCategoryDisplay === 'Show';
  const showSubcategories = form.subcategoryDisplay === 'Show';
  const showPublicFacing = form.publicFacingDisplay === 'Show';

  let scopePhrase = 'applicable Trade Categories and Subcategories';
  if (form.ruleScope === RULE_SCOPE.ALL_TRADE_CATEGORIES) {
    scopePhrase = 'all Trade Categories and Subcategories';
  } else if (form.ruleScope === RULE_SCOPE.SELECTED_TRADE_CATEGORIES) {
    const labels = (form.tradeCategoryIds || [])
      .map((id) => {
        const cat = (sources || []).find((c) => c.id === id);
        return cat ? cat.name : id;
      })
      .filter(Boolean);
    scopePhrase = labels.length ? labels.join(', ') : 'the selected Trade Categories';
  } else if (form.ruleScope === RULE_SCOPE.SELECTED_SUBCATEGORIES) {
    const labels = (form.subcategoryIds || [])
      .map((id) => {
        for (const cat of sources || []) {
          const sub = getTradeSubcategories(cat).find((s) => s.id === id);
          if (sub) return sub.name;
        }
        return id;
      })
      .filter(Boolean);
    scopePhrase = labels.length ? labels.join(', ') : 'the selected Subcategories';
  }

  const displayParts = [];
  if (showCategories) displayParts.push('show the Trade Category');
  else displayParts.push('hide the Trade Category');

  if (showPublicFacing && showSubcategories) {
    displayParts.push(
      'show public-facing Subcategories as standalone options and hide them from the standard Subcategory display',
    );
  } else if (showPublicFacing) {
    displayParts.push('show public-facing Subcategories as standalone options');
  } else if (showSubcategories) {
    displayParts.push('show standard Subcategories');
  } else {
    displayParts.push('hide the standard Subcategory display');
  }

  if (!showPublicFacing && showSubcategories) {
    displayParts.push('without standalone public-facing treatment');
  } else if (!showPublicFacing && !showSubcategories) {
    displayParts.push('and hide public-facing standalone options');
  }

  return `In ${area}, apply to ${scopePhrase}: ${displayParts.join('; ')}.`;
}

export function formatScopeSelectionDisplay(rule, sources = getTradeCategoryConfigSources()) {
  const scope = rule.ruleScope;
  if (scope === RULE_SCOPE.ALL_TRADE_CATEGORIES) {
    return ALL_SCOPE_SELECTION_LABEL;
  }

  if (scope === RULE_SCOPE.SELECTED_TRADE_CATEGORIES) {
    const ids = rule.tradeCategoryIds || [];
    if (!ids.length) return '—';
    return ids
      .map((id) => {
        const cat = (sources || []).find((c) => c.id === id);
        return cat ? formatSourceLabel(cat.name, cat.id) : id;
      })
      .join('; ');
  }

  if (scope === RULE_SCOPE.SELECTED_SUBCATEGORIES) {
    const subIds = rule.subcategoryIds || [];
    if (!subIds.length) return '—';
    return subIds
      .map((id) => {
        for (const cat of sources || []) {
          const sub = getTradeSubcategories(cat).find((s) => s.id === id);
          if (sub) return formatSourceLabel(sub.name, sub.id);
        }
        return id;
      })
      .join('; ');
  }

  return '—';
}

/**
 * Resolves permanent Trade Category and Subcategory IDs covered by a rule
 * for overlap validation. Selected Trade Categories include current children.
 */
export function resolveRuleSourceIds(rule, sources = getTradeCategoryConfigSources()) {
  if (!rule) {
    return {
      coversAll: false,
      tradeCategoryIds: new Set(),
      subcategoryIds: new Set(),
      parentIdsForSubs: new Set(),
    };
  }

  if (rule.ruleScope === RULE_SCOPE.ALL_TRADE_CATEGORIES) {
    return {
      coversAll: true,
      tradeCategoryIds: new Set(),
      subcategoryIds: new Set(),
      parentIdsForSubs: new Set(),
    };
  }

  if (rule.ruleScope === RULE_SCOPE.SELECTED_TRADE_CATEGORIES) {
    const tradeCategoryIds = new Set((rule.tradeCategoryIds || []).filter(Boolean));
    const subcategoryIds = new Set();
    (sources || []).forEach((category) => {
      if (!tradeCategoryIds.has(category.id)) return;
      getTradeSubcategories(category).forEach((sub) => {
        if (sub.id) subcategoryIds.add(sub.id);
      });
    });
    return {
      coversAll: false,
      tradeCategoryIds,
      subcategoryIds,
      parentIdsForSubs: new Set(),
    };
  }

  if (rule.ruleScope === RULE_SCOPE.SELECTED_SUBCATEGORIES) {
    const subcategoryIds = new Set((rule.subcategoryIds || []).filter(Boolean));
    const parentIdsForSubs = new Set((rule.parentTradeCategoryIds || []).filter(Boolean));
    const parentMap = new Map();
    (sources || []).forEach((category) => {
      getTradeSubcategories(category).forEach((sub) => {
        parentMap.set(sub.id, category.id);
      });
    });
    subcategoryIds.forEach((id) => {
      const parentId = parentMap.get(id);
      if (parentId) parentIdsForSubs.add(parentId);
    });
    return {
      coversAll: false,
      tradeCategoryIds: new Set(),
      subcategoryIds,
      parentIdsForSubs,
    };
  }

  return {
    coversAll: false,
    tradeCategoryIds: new Set(),
    subcategoryIds: new Set(),
    parentIdsForSubs: new Set(),
  };
}

export function normalizeTradeCategoryRule(raw = {}, sources = getTradeCategoryConfigSources()) {
  const base = {
    ...emptyTradeCategoryRuleForm(),
    ...raw,
    tradeCategoryIds: [...(raw.tradeCategoryIds || [])],
    parentTradeCategoryIds: [...(raw.parentTradeCategoryIds || [])],
    subcategoryIds: [...(raw.subcategoryIds || [])],
    changeHistory: [...(raw.changeHistory || [])],
    // Migrate legacy publicFacingTreatment → publicFacingDisplay when present
    publicFacingDisplay:
      raw.publicFacingDisplay ||
      (raw.publicFacingTreatment === 'Use Saved Public-Facing Treatment' ? 'Show' : '') ||
      (raw.publicFacingTreatment === 'Use Normal Source Values' ? 'Hide' : '') ||
      (raw.publicFacingTreatment === 'Not Applicable' ? 'Hide' : '') ||
      '',
  };
  delete base.priorityNumber;
  delete base.publicFacingTreatment;
  delete base.userAction;
  delete base.selectionRequired;
  delete base.hasBeenApplied;

  return {
    ...base,
    ruleBehaviour: buildRuleBehaviourSummary(base, sources),
    scopeSelectionDisplay: formatScopeSelectionDisplay(base, sources),
  };
}

export function createDefaultTradeCategoryRuleForm() {
  return emptyTradeCategoryRuleForm();
}

export function findTradeCategoryRule(rules, ruleId) {
  return (rules || []).find((r) => r.id === ruleId) ?? null;
}

export function findDuplicateRuleName(rules, ruleName, excludeId) {
  const name = String(ruleName || '').trim().toLowerCase();
  if (!name) return null;
  return (
    (rules || []).find(
      (r) =>
        r.id !== excludeId &&
        String(r.ruleName || '')
          .trim()
          .toLowerCase() === name,
    ) || null
  );
}

export function matchesTradeCategoryRuleSearch(rule, query) {
  const q = String(query || '')
    .trim()
    .toLowerCase();
  if (!q) return true;
  return (
    String(rule.id || '')
      .toLowerCase()
      .includes(q) ||
    String(rule.ruleName || '')
      .toLowerCase()
      .includes(q)
  );
}

export function matchesTradeCategoryRuleFilters(rule, { applicationAreaFilter, statusFilter } = {}) {
  if (applicationAreaFilter && applicationAreaFilter !== 'all') {
    if (rule.applicationArea !== applicationAreaFilter) return false;
  }
  if (statusFilter && statusFilter !== 'all') {
    if (rule.configurationStatus !== statusFilter) return false;
  }
  return true;
}

export function getListActionsForTradeCategoryRule() {
  return ['view', 'edit'];
}

export function buildSampleTradeCategoryConfigRules() {
  const sources = getTradeCategoryConfigSources();
  const plumbing = sources.find((c) => /plumbing/i.test(c.name)) || sources[0];
  const plumbingId = plumbing?.id || 'TC-001';
  const subs = getTradeSubcategories(plumbing || {});
  const general = subs.find((s) => /general plumbing/i.test(s.name)) || subs[0];
  const gas = subs.find((s) => /gas fitting/i.test(s.name)) || subs[1];
  const hotWater = subs.find((s) => /hot water/i.test(s.name)) || subs[2];
  const stamp = '2026-08-03T07:30:00.000Z';
  const actor = 'Platform Owner';

  const samples = [
    {
      id: 'CTC-0001',
      ruleName: 'Business Signup — All Trade Categories',
      applicationArea: 'Business Signup',
      ruleScope: RULE_SCOPE.ALL_TRADE_CATEGORIES,
      tradeCategoryIds: [],
      parentTradeCategoryIds: [],
      subcategoryIds: [],
      tradeCategoryDisplay: 'Show',
      subcategoryDisplay: 'Hide',
      publicFacingDisplay: 'Hide',
      configurationStatus: CONFIGURATION_STATUS.ACTIVE,
      createdBy: actor,
      createdOn: stamp,
      lastUpdatedBy: actor,
      lastUpdatedOn: stamp,
      changeHistory: [
        {
          action: 'Created',
          previousValues: null,
          newValues: { configurationStatus: CONFIGURATION_STATUS.ACTIVE },
          by: actor,
          on: stamp,
        },
      ],
    },
    {
      id: 'CTC-0002',
      ruleName: 'Business Profile — Selected Plumbing Services',
      applicationArea: 'Business Profile',
      ruleScope: RULE_SCOPE.SELECTED_SUBCATEGORIES,
      tradeCategoryIds: [],
      parentTradeCategoryIds: [plumbingId],
      subcategoryIds: [general?.id, gas?.id, hotWater?.id].filter(Boolean),
      tradeCategoryDisplay: 'Show',
      subcategoryDisplay: 'Show',
      publicFacingDisplay: 'Show',
      configurationStatus: CONFIGURATION_STATUS.ACTIVE,
      createdBy: actor,
      createdOn: stamp,
      lastUpdatedBy: actor,
      lastUpdatedOn: stamp,
      changeHistory: [
        {
          action: 'Created',
          previousValues: null,
          newValues: { configurationStatus: CONFIGURATION_STATUS.ACTIVE },
          by: actor,
          on: stamp,
        },
      ],
    },
    {
      id: 'CTC-0003',
      ruleName: 'Post Creation — Plumbing',
      applicationArea: 'Post Creation',
      ruleScope: RULE_SCOPE.SELECTED_TRADE_CATEGORIES,
      tradeCategoryIds: [plumbingId],
      parentTradeCategoryIds: [],
      subcategoryIds: [],
      tradeCategoryDisplay: 'Show',
      subcategoryDisplay: 'Show',
      publicFacingDisplay: 'Hide',
      configurationStatus: CONFIGURATION_STATUS.ACTIVE,
      createdBy: actor,
      createdOn: stamp,
      lastUpdatedBy: actor,
      lastUpdatedOn: stamp,
      changeHistory: [
        {
          action: 'Created',
          previousValues: null,
          newValues: { configurationStatus: CONFIGURATION_STATUS.ACTIVE },
          by: actor,
          on: stamp,
        },
      ],
    },
    {
      id: 'CTC-0004',
      ruleName: 'Public Browse — Plumbing Public-Facing',
      applicationArea: 'Public Browse',
      ruleScope: RULE_SCOPE.SELECTED_TRADE_CATEGORIES,
      tradeCategoryIds: [plumbingId],
      parentTradeCategoryIds: [],
      subcategoryIds: [],
      tradeCategoryDisplay: 'Show',
      subcategoryDisplay: 'Hide',
      publicFacingDisplay: 'Show',
      configurationStatus: CONFIGURATION_STATUS.ACTIVE,
      createdBy: actor,
      createdOn: stamp,
      lastUpdatedBy: actor,
      lastUpdatedOn: stamp,
      changeHistory: [
        {
          action: 'Created',
          previousValues: null,
          newValues: { configurationStatus: CONFIGURATION_STATUS.ACTIVE },
          by: actor,
          on: stamp,
        },
      ],
    },
  ];

  return samples.map((row) => normalizeTradeCategoryRule(row, sources));
}
